/**
 * Cadastro e edição de posto em etapas (rotas /ergonomic-workstations/new e /:id/edit) — AET, #356.
 * Tarefa: descrever o posto pelo que se viu no local, parando e continuando quando quiser.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress } from '@mui/material';
import { PageShell } from '../../components';
import { AvisoDeFalhaAoCarregar } from '../../components/AvisoDeFalhaAoCarregar';
import { FalhaDeCarga, falhaDeCarga, textoDaFalhaDeCarga } from '../../utils/acessoNegado';
import { useListaPermitida } from '../../authorization/useListaPermitida';
import { useAcao } from '../../authorization/useVeredito';
import { departmentService } from '../../services/departmentService';
import { ergonomicWorkstationService } from '../../services/ergonomicWorkstationService';
import type { ErgonomicOptions, ErgonomicWorkstationDetail } from '../../types/ergonomicWorkstation';
import type { Department } from '../../types/department';
import { Wizard } from './components/Wizard';
import { errorMessage } from './components/Wizard/wizardValidation';

const LIST_PATH = '/ergonomic-workstations';

/** Os setores do cadastro; só são pedidos a quem pode ler setores (`useListaPermitida`). */
const loadDepartments = async (companyId: string): Promise<readonly Department[]> =>
  (await departmentService.getDepartments({ limit: 200, ...(companyId === '' ? {} : { companyId }) })).data;

const ErgonomicWorkstationFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const create = useAcao('ergonomic_workstation.create', 'Cadastrar posto');
  const update = useAcao('ergonomic_workstation.update', 'Editar posto');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [falha, setFalha] = useState<FalhaDeCarga | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [workstation, setWorkstation] = useState<ErgonomicWorkstationDetail | undefined>(undefined);
  const [options, setOptions] = useState<ErgonomicOptions | null>(null);

  // Cada etapa grava com `update`; a criação ainda exige `create` para o POST da etapa 1.
  const allowed = id ? !update.disabled : !create.disabled && !update.disabled;
  // Com empresa escolhida na etapa 1, só os setores dela são buscados; sem escolha, vale a lista toda.
  const [companyId, setCompanyId] = useState('');
  const loadCompanyDepartments = useCallback(() => loadDepartments(companyId), [companyId]);
  const departmentList = useListaPermitida('department.read', 'Setores', loadCompanyDepartments, {
    habilitado: allowed,
  });
  const departments = useMemo(() => [...departmentList.itens], [departmentList.itens]);

  useEffect(() => {
    if (!allowed) {
      setLoading(false);
      return undefined;
    }
    let active = true;
    setLoading(true);

    const load = async () => {
      try {
        const carregarPosto = async (): Promise<ErgonomicWorkstationDetail | undefined> => {
          if (!id) return undefined;
          try {
            return await ergonomicWorkstationService.getById(id);
          } catch (err: unknown) {
            const falhaDoPosto = falhaDeCarga(err, 'Não foi possível carregar o posto. Tente de novo.');
            // Sem acesso e inexistente não melhoram tentando de novo; o resto segue para o
            // "Tentar de novo" de baixo.
            if (falhaDoPosto.status !== 403 && falhaDoPosto.status !== 404) throw err;
            if (active) setFalha(falhaDoPosto);
            return undefined;
          }
        };
        setFalha(null);
        const [found, listas] = await Promise.all([carregarPosto(), ergonomicWorkstationService.options()]);
        if (!active) return;
        setWorkstation(found);
        setOptions(listas);
        setError(null);
      } catch (err: unknown) {
        if (active) setError(errorMessage(err, 'Não foi possível abrir o posto.'));
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [id, allowed, attempt]);

  const breadcrumbs = [
    { label: 'Início', path: '/' },
    { label: 'Análise ergonômica', path: LIST_PATH },
    { label: id ? (workstation?.name ?? 'Editar posto') : 'Novo posto' },
  ];
  const title = id ? 'Editar posto de trabalho' : 'Novo posto de trabalho';

  if (!allowed) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title={title}>
        <Alert
          severity="info"
          sx={{ fontSize: '1rem' }}
          action={
            <Button color="inherit" onClick={() => navigate(LIST_PATH)} sx={{ minHeight: 44 }}>
              Voltar para os postos
            </Button>
          }
        >
          Seu acesso permite ver os postos, mas não {id ? 'alterá-los' : 'cadastrar novos'}. Peça a quem administra o
          seu acesso.
        </Alert>
      </PageShell>
    );
  }

  if (id && !loading && falha) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title={title}>
        <AvisoDeFalhaAoCarregar
          mensagem={textoDaFalhaDeCarga(falha, 'Posto não encontrado.')}
          rotuloDoBotao="Voltar para os postos"
          aoVoltar={() => navigate(LIST_PATH)}
        />
      </PageShell>
    );
  }

  return (
    <PageShell
      breadcrumbs={breadcrumbs}
      title={title}
      subtitle="Preencha pelo que você viu no local. Dá para parar e continuar depois."
    >
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress aria-label="Carregando" />
        </Box>
      )}
      {!loading && error && (
        <Alert
          severity="error"
          sx={{ fontSize: '1rem' }}
          action={
            <Button color="inherit" onClick={() => setAttempt((value) => value + 1)} sx={{ minHeight: 44 }}>
              Tentar de novo
            </Button>
          }
        >
          {error}
        </Alert>
      )}
      {!loading && !error && (
        <Wizard
          initialWorkstation={workstation}
          options={options}
          departments={departments}
          departmentsLoading={!departmentList.resolvida}
          departmentsBlocked={departmentList.bloqueio}
          departmentsFailed={
            departmentList.erro ? 'Não foi possível carregar os setores. Recarregue a página para tentar de novo.' : undefined
          }
          onCompanyChange={setCompanyId}
          onFinish={() => navigate(LIST_PATH)}
          onCancel={() => navigate(LIST_PATH)}
        />
      )}
    </PageShell>
  );
};

export default ErgonomicWorkstationFormPage;
